import { CookiePermissionInfo, CookieWallContent, CookieWallSettings, SettingsCategory } from "./models/cookie-wall.models";
import Cookies, { CookieAttributes } from 'js-cookie';
import { WidgetUtil } from "../../services/widgets.util";

const COOKIE_PERMISSION_COOKIE = 'CookiePermissionInfo';

/// For backwards-compatilibty: overwrite the default converters with the decoding algorithms used by the previous cookie-widget
const CookieStore = Cookies.withConverter({
    read: (value, name) => {
        return name === COOKIE_PERMISSION_COOKIE
            ? decodeURIComponent(value)
            : Cookies.converter.read(value, name);
    },
    write: (value, name) => {
        return name === COOKIE_PERMISSION_COOKIE
            ? encodeURIComponent(value)
            : Cookies.converter.write(value, name);
    }
});

export async function getCookieWidgetContent(domain: string | undefined, locale: string): Promise<CookieWallContent> {
    const cookieDomain = domain || window.location.hostname;
    const apiDomain = WidgetUtil.rootUrl();
    const backendLocale = locale.replace('_', '-');

    console.debug(`Fetching cookie wall content for ${cookieDomain}, ${backendLocale}`);
    const content = await fetch(`${apiDomain}/api/cookiewalls?domain=${cookieDomain}&locale=${backendLocale}`)
        .then(response => response.json())
        .then(content => content as CookieWallContent)

    return content;
}

export function hasCookie(): boolean {
    const currentCookie = getRawCookie();
    return (currentCookie !== undefined);
}

function getRawCookie(): string | undefined {
    const currentCookie = CookieStore.get(COOKIE_PERMISSION_COOKIE);
    return currentCookie
        ? decodeURI(currentCookie)
        : undefined;
}

export function getCookieSettings(cookieDomain: string, settingsCategories: SettingsCategory[]): CookieWallSettings {
    const cookie = CookieStore.get(COOKIE_PERMISSION_COOKIE);
    const permissions = cookie ? JSON.parse(cookie) as CookiePermissionInfo : undefined;

    const categories = settingsCategories.map(cat => {
        const permission = permissions?.CategoryPermission?.find(p => p.Category === cat.key);
        const checked = permission?.Permission || cat.required;

        return {
            title: cat.title,
            category: cat.key,
            description: cat.description,
            required: cat.required,
            checked
        }
    });

    return {
        cookieDomain,
        categories
    };
}

export function saveCookieSettings(settings: CookieWallSettings): void {
    const lastModifiedDate = new Date();
    const expirationDate = new Date(lastModifiedDate);
    expirationDate.setFullYear(lastModifiedDate.getFullYear() + 1);

    const permission: CookiePermissionInfo = {
        LastModifiedDate: lastModifiedDate,
        ExpirationDate: expirationDate,
        Allow: settings.categories.some(c => c.checked),
        CategoryPermission: settings.categories.map(cat => ({
            Category: cat.category,
            Permission: cat.checked
        }))
    };

    saveCookie(permission, settings.cookieDomain);

    document.body.dispatchEvent(new CustomEvent<object>('pnl_cookiepermission_changed', {
        detail: {
            permission
        }
    }));
}

export function isOldCookie(): boolean {
    const rawCookie = getRawCookie();
    return rawCookie.includes("Date(");
}

export function reformatOldCookie(content: CookieWallContent) {
    const rawCookie = getRawCookie();

    const oldCookie = JSON.parse(rawCookie);
    const lastModifiedDate = formatCookieDate(oldCookie.LastModifiedDate);
    const expirationDate = formatCookieDate(oldCookie.ExpirationDate);

    const permission: CookiePermissionInfo = {
        LastModifiedDate: lastModifiedDate,
        ExpirationDate: expirationDate,
        Allow: oldCookie.CategoryPermission.some(c => c.Permission === true),
        CategoryPermission: oldCookie.CategoryPermission.map(cat => ({
            Category: cat.Category,
            Permission: cat.Permission
        }))
    };

    console.info('Overwriting deprecated cookie format with new format');
    saveCookie(permission, content.cookieDomain);
}

function formatCookieDate(oldFormat: string): Date {
    var filteredNumbers = oldFormat.match(/\d+/g)[0];
    return new Date(Number(filteredNumbers));
}

export function saveCookie(permission: CookiePermissionInfo, cookieDomain: string): void {
    console.info(`Store permission info cookie on domain ${cookieDomain}`, permission);

    const options: CookieAttributes = cookieDomain === '*'
        ? { expires: permission.ExpirationDate }
        : { expires: permission.ExpirationDate, domain: cookieDomain };

    const json = JSON.stringify(permission);
    CookieStore.set(COOKIE_PERMISSION_COOKIE, json, options);
}
